import { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = ({ variant = 'full' }) => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    age_eleve: '',
    instrument: '',
    niveau: '',
    disponibilites: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.nom.trim()) {
      setError('Veuillez entrer votre nom');
      return;
    }
    if (!formData.email && !formData.telephone) {
      setError('Veuillez entrer un email ou un numéro de téléphone');
      return;
    }
    if (!formData.instrument) {
      setError('Veuillez sélectionner un instrument ou activité');
      return;
    }
    if (!formData.niveau) {
      setError('Veuillez sélectionner votre niveau');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        age_eleve: '',
        instrument: '',
        niveau: '',
        disponibilites: '',
        message: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-teal/10 border border-teal/30 rounded-2xl p-8 text-center" data-testid="contact-success">
        <div className="w-16 h-16 bg-teal rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h3 className="font-dancing text-3xl text-teal mb-2">Merci pour votre message !</h3>
        <p className="text-gray-600 mb-6">
          Notre équipe vous recontactera très rapidement pour planifier votre cours d'essai.
        </p>
        <button
          onClick={() => setIsSuccess(false)}
          className="px-6 py-3 bg-burgundy text-white rounded-full font-semibold hover:bg-burgundy-600 transition-all"
          data-testid="contact-new-request-btn"
        >
          Envoyer une nouvelle demande
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="contact-error">
          {error}
        </div>
      )}

      {/* Nom */}
      <div className="space-y-2">
        <Label htmlFor="nom" className="text-gray-700 font-semibold">
          Nom complet <span className="text-coral">*</span>
        </Label>
        <Input
          id="nom"
          name="nom"
          type="text"
          value={formData.nom}
          onChange={handleChange}
          placeholder="Votre nom"
          className="rounded-xl border-gray-200 focus:border-burgundy focus:ring-burgundy"
          data-testid="contact-nom-input"
        />
      </div>

      {/* Email & Téléphone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-gray-700 font-semibold">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="votre@email.com"
            className="rounded-xl border-gray-200 focus:border-burgundy focus:ring-burgundy"
            data-testid="contact-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="telephone" className="text-gray-700 font-semibold">
            Téléphone
          </Label>
          <Input
            id="telephone"
            name="telephone"
            type="tel"
            value={formData.telephone}
            onChange={handleChange}
            placeholder="06 XX XX XX XX"
            className="rounded-xl border-gray-200 focus:border-burgundy focus:ring-burgundy"
            data-testid="contact-telephone-input"
          />
        </div>
      </div>

      {/* Âge & Instrument */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="age_eleve" className="text-gray-700 font-semibold">
            Âge de l'élève
          </Label>
          <Input
            id="age_eleve"
            name="age_eleve"
            type="text"
            value={formData.age_eleve}
            onChange={handleChange}
            placeholder="ex: 7 ans"
            className="rounded-xl border-gray-200 focus:border-burgundy focus:ring-burgundy"
            data-testid="contact-age-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-gray-700 font-semibold">
            Instrument / Activité <span className="text-coral">*</span>
          </Label>
          <Select
            value={formData.instrument}
            onValueChange={(value) => handleSelectChange('instrument', value)}
          >
            <SelectTrigger className="rounded-xl border-gray-200 focus:border-burgundy focus:ring-burgundy" data-testid="contact-instrument-select">
              <SelectValue placeholder="Choisir..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="eveil">Éveil musical (3-8 ans)</SelectItem>
              <SelectItem value="piano">Piano</SelectItem>
              <SelectItem value="guitare">Guitare</SelectItem>
              <SelectItem value="chant">Chant</SelectItem>
              <SelectItem value="atelier">Atelier sensoriel</SelectItem>
              <SelectItem value="autre">Autre / Je ne sais pas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Niveau & Disponibilités */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-gray-700 font-semibold">
            Niveau <span className="text-coral">*</span>
          </Label>
          <Select
            value={formData.niveau}
            onValueChange={(value) => handleSelectChange('niveau', value)}
          >
            <SelectTrigger className="rounded-xl border-gray-200 focus:border-burgundy focus:ring-burgundy" data-testid="contact-niveau-select">
              <SelectValue placeholder="Choisir..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="debutant">Débutant complet</SelectItem>
              <SelectItem value="initie">Quelques bases</SelectItem>
              <SelectItem value="intermediaire">Intermédiaire</SelectItem>
              <SelectItem value="avance">Avancé</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="disponibilites" className="text-gray-700 font-semibold">
            Disponibilités
          </Label>
          <Input
            id="disponibilites"
            name="disponibilites"
            type="text"
            value={formData.disponibilites}
            onChange={handleChange}
            placeholder="ex: Mercredi après-midi"
            className="rounded-xl border-gray-200 focus:border-burgundy focus:ring-burgundy"
            data-testid="contact-disponibilites-input"
          />
        </div>
      </div>

      {/* Message */}
      {variant === 'full' && (
        <div className="space-y-2">
          <Label htmlFor="message" className="text-gray-700 font-semibold">
            Votre message (optionnel)
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Parlez-nous de votre projet musical, de vos attentes..."
            rows={4}
            className="rounded-xl border-gray-200 focus:border-burgundy focus:ring-burgundy resize-none"
            data-testid="contact-message-textarea"
          />
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full px-8 py-4 bg-burgundy text-white rounded-full font-bold text-lg hover:bg-burgundy-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="contact-submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Envoi en cours...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Demander un essai gratuit
          </>
        )}
      </button>

      <p className="text-center text-gray-500 text-sm">
        En soumettant ce formulaire, vous acceptez d'être recontacté(e) par notre équipe.
      </p>
    </form>
  );
};
